import { Box, LinearProgress } from "@mui/material";
import { Suspense, createContext, useCallback, useContext, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { usePermissions } from "../../../server/auth/use-permissions";
import { useFlag } from "../../../server/optimizely";
import { HeaderBar } from "../../generic/components/header-bar";
import { HeaderBarTitle } from "../../generic/components/header-bar-title";
import { useMomentHeader } from "../../moment/hooks/use-moment-header";
import { MomentPageNavigation } from "./moment-page-navigation";
import { getMomentPageSections } from "./moment-page-sections";

import type { UUID } from "../../../utils/uuid";
import type { MomentHeaderData } from "../../moment/hooks/use-moment-header";
import type { ReviewStatus } from "../../moment/server/supabase-moment-review-service";
import type { FC } from "react";

interface MomentPageShellValue {
  header: MomentHeaderData | null;
  reloadHeader: () => void;
  headerTarget: HTMLDivElement | null;
  setPortaledHeaderActive: (active: boolean) => void;
  setReviewStatus: (status: ReviewStatus | null) => void;
}

const MomentPageShellContext = createContext<MomentPageShellValue | null>(null);

/** Null on legacy routes and in isolated component tests. */
export const useMomentPageShell = (): MomentPageShellValue | null =>
  useContext(MomentPageShellContext);

export const MomentPageShell: FC = () => {
  const [enabled, , flagLoaded] = useFlag("reviewer_role");

  if (flagLoaded && !enabled)
    return (
      <Suspense fallback={<LinearProgress />}>
        <Outlet />
      </Suspense>
    );
  return <EnabledMomentPageShell flagLoaded={flagLoaded} />;
};

const EnabledMomentPageShell: FC<{ flagLoaded: boolean }> = ({ flagLoaded }) => {
  const { id } = useParams<{ id: UUID }>();
  if (!id) return null;
  return <LoadedMomentPageShell flagLoaded={flagLoaded} momentId={id} />;
};

const LoadedMomentPageShell: FC<{ flagLoaded: boolean; momentId: UUID }> = ({
  flagLoaded,
  momentId,
}) => {
  const { reviewer, ce_admin, isLoading: permissionsLoading } = usePermissions();
  const { data, loading, reload } = useMomentHeader(momentId, !permissionsLoading);
  const { pathname } = useLocation();
  const [headerTarget, setHeaderTarget] = useState<HTMLDivElement | null>(null);
  const [portaledHeaderActive, setPortaledHeaderActive] = useState(false);
  const [reviewStatusOverride, setReviewStatusOverride] = useState<{
    momentId: UUID;
    status: ReviewStatus | null;
  } | null>(null);
  const setReviewStatus = useCallback(
    (status: ReviewStatus | null) => setReviewStatusOverride({ momentId, status }),
    [momentId],
  );
  const header = data?.id === momentId ? data : null;
  const reviewing = /\/review(\/|$)/.test(pathname);
  const newsletter =
    pathname === `/moments/${momentId}/newsletter` ||
    pathname.startsWith(`/moments/${momentId}/newsletter/`);
  const base = `/moments/${momentId}/${newsletter ? "newsletter/" : ""}`;
  const [pathSection] = pathname.slice(base.length).split("/");
  const section = pathSection ?? "";
  const reviewerRedirect = reviewer && section !== "review";
  const requiresPublishedMoment = ["analytics", "poll_results", "outbox"].includes(section);
  const disallowedSection =
    !reviewer &&
    header &&
    requiresPublishedMoment &&
    !getMomentPageSections(header, ce_admin).includes(
      section as "analytics" | "poll_results" | "outbox",
    );
  const contextValue = useMemo(
    () => ({
      header,
      reloadHeader: reload,
      headerTarget,
      setPortaledHeaderActive,
      setReviewStatus,
    }),
    [header, reload, headerTarget, setReviewStatus],
  );

  return (
    <MomentPageShellContext.Provider value={contextValue}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <HeaderBar navBurger>
          <Box
            ref={setHeaderTarget}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {!portaledHeaderActive && (
              <HeaderBarTitle
                title={{ type: "fixed", value: header?.title ?? "New Communication" }}
                subtitle={header?.program_title}
                channel={header?.channel ?? undefined}
                status={header?.status}
                reviewStatus={
                  reviewing
                    ? reviewStatusOverride?.momentId === momentId
                      ? (reviewStatusOverride.status ?? undefined)
                      : (header?.review_status ?? undefined)
                    : undefined
                }
                loading={loading && !header}
              />
            )}
          </Box>
        </HeaderBar>
        {!reviewer && header && <MomentPageNavigation header={header} ceAdmin={ce_admin} />}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Suspense key={pathname} fallback={<LinearProgress />}>
            {!flagLoaded ||
            permissionsLoading ||
            (loading && section !== "details" && section !== "") ? (
              <LinearProgress />
            ) : reviewerRedirect ? (
              <Navigate replace to={`${base}review`} />
            ) : disallowedSection || (!reviewer && requiresPublishedMoment && !header) ? (
              <Navigate replace to={`${base}details`} />
            ) : (
              <Outlet />
            )}
          </Suspense>
        </Box>
      </Box>
    </MomentPageShellContext.Provider>
  );
};
